import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Searchable Select Component
const SearchableSelect = ({ options, value, onChange, placeholder, dropdownKey, openDropdown, setOpenDropdown, searchValue, setSearchValue }) => {
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchValue[dropdownKey] || '')
  );

  return (
    <div className="relative w-full">
      <input
        type="text"
        placeholder={placeholder}
        value={value && value.label ? value.label : searchValue[dropdownKey] || ''}
        onChange={(e) => {
          setSearchValue(prev => ({
            ...prev,
            [dropdownKey]: e.target.value
          }));
          setOpenDropdown(dropdownKey);
        }}
        onFocus={() => setOpenDropdown(dropdownKey)}
        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
        style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}
      />
      {openDropdown === dropdownKey && filteredOptions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto" style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}>
          {filteredOptions.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => {
                onChange(opt);
                setOpenDropdown(null);
                setSearchValue(prev => ({
                  ...prev,
                  [dropdownKey]: ''
                }));
              }}
              className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b last:border-b-0 text-sm"
              style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Recipe Modal Component
const RecipeModal = ({ isOpen, onClose, recipe }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-900">{recipe?.name || 'Recipe'}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {recipe?.video_id && (
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800 mb-2">Source:</h4>
              <a
                href={`https://youtube.com/watch?v=${recipe.video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
                Watch on YouTube
              </a>
            </div>
          )}

          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Nutritional Info:</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm bg-gray-50 p-3 rounded-lg">
              <div className="text-center">
                <div className="font-medium">{recipe?.calories || 0}</div>
                <div className="text-xs text-gray-500">Calories</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{recipe?.protein || 0}g</div>
                <div className="text-xs text-gray-500">Protein</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{recipe?.carbs || 0}g</div>
                <div className="text-xs text-gray-500">Carbs</div>
              </div>
              <div className="text-center">
                <div className="font-medium">{recipe?.fat || 0}g</div>
                <div className="text-xs text-gray-500">Fat</div>
              </div>
            </div>
          </div>

          {recipe?.recipe ? (
            <>
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">Ingredients:</h4>
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {Array.isArray(recipe.recipe.ingredients)
                    ? recipe.recipe.ingredients.map((ingredient, idx) => (
                        <li key={idx} className="text-sm">{ingredient}</li>
                      ))
                    : <li className="text-sm">No ingredients available</li>
                  }
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Instructions:</h4>
                <div className="prose max-w-none text-gray-700 text-sm whitespace-pre-line">
                  {recipe.recipe.recipe}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-gray-500">
              Recipe details not available for this item.
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomWeeklyPlanPage = () => {
  const { user, guestMode } = useAuth();
  const navigate = useNavigate();

  // State management
  const [filterRegion, setFilterRegion] = useState('');
  const [filterFoodType, setFilterFoodType] = useState('');
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Days with their meal selections
  const [planDays, setPlanDays] = useState([
    {
      id: 1,
      date: new Date(),
      meals: {
        breakfast: [],
        lunch: [],
        dinner: [],
        snack: []
      }
    }
  ]);

  // State for searchable dropdowns
  const [dropdownSearch, setDropdownSearch] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);

  // Available options for filters
  const regions = ['North', 'South', 'East', 'West', 'All'];
  const foodTypes = ['veg', 'non-veg', 'vegan'];
  const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
  const mealEmojis = {
    breakfast: '🌅',
    lunch: '🌞',
    dinner: '🌙',
    snack: '🍪'
  };

  // Fetch foods based on filters
  const fetchFoods = async () => {
    if (!filterRegion && !filterFoodType) {
      toast.info('Please select at least one filter (Region or Food Type)');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterRegion) params.append('region', filterRegion);
      if (filterFoodType) params.append('food_type', filterFoodType);

      const response = await axios.get(`${API}/foods/database?${params.toString()}`);
      setFoods(response.data);
      toast.success(`Found ${response.data.length} foods matching your filters`);
    } catch (error) {
      console.error('Error fetching foods:', error);
      toast.error('Failed to fetch foods: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle view recipe
  const handleViewRecipe = (meal) => {
    setSelectedRecipe(meal);
    setIsModalOpen(true);
  };

  // Add a new day card
  const addDay = () => {
    const newId = Math.max(...planDays.map(d => d.id), 0) + 1;
    const newDate = new Date(planDays[planDays.length - 1].date);
    newDate.setDate(newDate.getDate() + 1);

    setPlanDays([
      ...planDays,
      {
        id: newId,
        date: newDate,
        meals: {
          breakfast: [],
          lunch: [],
          dinner: [],
          snack: []
        }
      }
    ]);
  };

  // Remove a day card
  const removeDay = (dayId) => {
    if (planDays.length === 1) {
      toast.warning('You must have at least one day in your plan');
      return;
    }
    setPlanDays(planDays.filter(d => d.id !== dayId));
  };

  // Add a meal to the plan
  const addMeal = (dayId, mealType, meal) => {
    setPlanDays(planDays.map(day => {
      if (day.id === dayId) {
        const updatedMeals = {
          ...day.meals,
          [mealType]: [...(day.meals[mealType] || []), meal]
        };
        return { ...day, meals: updatedMeals };
      }
      return day;
    }));
  };

  // Remove a meal from the plan
  const removeMeal = (dayId, mealType, mealId) => {
    setPlanDays(planDays.map(day => {
      if (day.id === dayId) {
        const updatedMeals = {
          ...day.meals,
          [mealType]: day.meals[mealType].filter(meal => meal.id !== mealId)
        };
        return { ...day, meals: updatedMeals };
      }
      return day;
    }));
  };

  // Update date for a day
  const updateDate = (dayId, newDate) => {
    setPlanDays(planDays.map(day =>
      day.id === dayId
        ? { ...day, date: newDate }
        : day
    ));
  };

  // Save weekly plan
  const savePlan = async () => {
    if (!user) {
      toast.error('Please log in to save your plan');
      return;
    }

    // Prompt user for plan name
    const planName = prompt('Enter a name for your meal plan:', `My Weekly Plan - ${new Date().toLocaleDateString()}`);
    if (!planName) {
      toast.info('Plan save cancelled');
      return;
    }

    setLoading(true);
    try {
      const planData = {
        user_id: user.id,
        name: planName,  // Add plan name
        type: 'custom_weekly',  // Set plan type
        meals: [],
        week_plan: {}
      };

      const overallTotals = { calories: 0, protein: 0, carbs: 0, fat: 0 };

      // Organize meals by day
      planDays.forEach((day, dayIndex) => {
        // Format date properly without timezone conversion
        const dateStr = day.date.getFullYear() + '-' +
                       String(day.date.getMonth() + 1).padStart(2, '0') + '-' +
                       String(day.date.getDate()).padStart(2, '0');

        planData.week_plan[dayIndex] = {
          date: dateStr,
          meals: {}
        };

        mealTypes.forEach(mealType => {
          if (day.meals[mealType]?.length > 0) {
            // Store all meals for this meal type
            planData.week_plan[dayIndex].meals[mealType] = day.meals[mealType].map(meal => ({
              id: meal.id,
              name: meal.name,
              calories: meal.calories,
              protein: meal.protein,
              carbs: meal.carbs,
              fat: meal.fat
            }));

            // Add each meal to the flat meals list
            day.meals[mealType].forEach(meal => {
              planData.meals.push({
                id: meal.id,
                name: meal.name,
                calories: meal.calories,
                protein: meal.protein,
                carbs: meal.carbs,
                fat: meal.fat,
                meal_type: mealType,
                day_index: dayIndex,
                date: dateStr
              });

              // Add to overall totals
              overallTotals.calories += meal.calories || 0;
              overallTotals.protein += meal.protein || 0;
              overallTotals.carbs += meal.carbs || 0;
              overallTotals.fat += meal.fat || 0;
            });
          }
        });
      });

      // Add totals to the payload
      planData.total_calories = Math.round(overallTotals.calories);
      planData.total_protein = Math.round(overallTotals.protein);
      planData.total_carbs = Math.round(overallTotals.carbs);
      planData.total_fat = Math.round(overallTotals.fat);

      // Add date range for weekly plan
      if (planDays.length > 0) {
        // Get first day's date
        const firstDay = planDays[0];
        const startDateStr = firstDay.date.getFullYear() + '-' +
                            String(firstDay.date.getMonth() + 1).padStart(2, '0') + '-' +
                            String(firstDay.date.getDate()).padStart(2, '0');

        // Get last day's date
        const lastDay = planDays[planDays.length - 1];
        const endDateStr = lastDay.date.getFullYear() + '-' +
                          String(lastDay.date.getMonth() + 1).padStart(2, '0') + '-' +
                          String(lastDay.date.getDate()).padStart(2, '0');

        planData.start_date = startDateStr;
        planData.end_date = endDateStr;
      }

      const response = await axios.post(`${API}/meal-plans/save`, planData);
      toast.success('✅ Weekly plan saved successfully!');

      setTimeout(() => {
        navigate('/saved-plans');
      }, 1500);
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Failed to save plan: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for a day
  const calculateDayTotals = (day) => {
    let totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    Object.values(day.meals).forEach(meals => {
      if (Array.isArray(meals)) {
        meals.forEach(meal => {
          if (meal) {
            totals.calories += meal.calories || 0;
            totals.protein += meal.protein || 0;
            totals.carbs += meal.carbs || 0;
            totals.fat += meal.fat || 0;
          }
        });
      }
    });
    return totals;
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, rgba(243, 234, 255, 0.8) 0%, rgba(255, 232, 240, 0.8) 25%, rgba(232, 244, 255, 0.8) 50%, rgba(255, 232, 213, 0.8) 75%, rgba(243, 234, 255, 0.8) 100%)' }}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h4 className="heading-4" style={{ color: 'rgb(24, 24, 24)' }}>🎨 Custom Meal Planner</h4>
          <p className="body-medium" style={{ color: 'rgb(112, 112, 112)' }}>Tailor your meal plan with specific filters</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Panel - Filters */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h2 className="heading-4 mb-4" style={{ color: 'rgb(24, 24, 24)' }}>🔍 Filters</h2>

              {/* Region Filter */}
              <div className="mb-4">
                <label className="body-medium font-semibold mb-2 block" style={{ color: 'rgb(24, 24, 24)' }}>Region</label>
                <SearchableSelect
                  options={regions.map(r => ({ label: r, value: r }))}
                  value={filterRegion ? { label: filterRegion, value: filterRegion } : null}
                  onChange={(opt) => setFilterRegion(opt.value)}
                  placeholder="Search region..."
                  dropdownKey="region"
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  searchValue={dropdownSearch}
                  setSearchValue={setDropdownSearch}
                />
              </div>

              {/* Food Type Filter */}
              <div className="mb-6">
                <label className="body-medium font-semibold mb-2 block" style={{ color: 'rgb(24, 24, 24)' }}>Food Type</label>
                <SearchableSelect
                  options={foodTypes.map(t => ({ label: t, value: t }))}
                  value={filterFoodType ? { label: filterFoodType, value: filterFoodType } : null}
                  onChange={(opt) => setFilterFoodType(opt.value)}
                  placeholder="Search food type..."
                  dropdownKey="foodType"
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  searchValue={dropdownSearch}
                  setSearchValue={setDropdownSearch}
                />
              </div>

              {/* Fetch Foods Button */}
              <button
                onClick={fetchFoods}
                disabled={loading || (!filterRegion && !filterFoodType)}
                className="w-full py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 transition-colors font-medium"
              >
                {loading ? 'Loading...' : '🔍 Search Foods'}
              </button>

              {/* Foods List */}
              {foods.length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <h3 className="heading-4 mb-3" style={{ color: 'rgb(24, 24, 24)' }}>📋 Foods ({foods.length})</h3>
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {foods.map((food, idx) => (
                      <div key={idx} className="p-2 rounded-lg border text-xs" style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}>
                        <p className="font-semibold" style={{ color: 'rgb(24, 24, 24)' }}>{food.name}</p>
                        <p style={{ color: 'rgb(112, 112, 112)' }}>{food.calories}cal | P:{food.protein}g</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Weekly Plan Cards */}
          <div className="lg:col-span-3">
            <div className="space-y-4">
              {planDays.map((day) => {
                const dayTotals = calculateDayTotals(day);
                return (
                  <div key={day.id} className="bg-white rounded-2xl shadow-lg p-6">
                    {/* Day Header with Date */}
                    <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}>
                      <div className="flex-1">
                        <label className="body-medium font-semibold mb-2 block" style={{ color: 'rgb(24, 24, 24)' }}>Date</label>
                        <DatePicker
                          selected={day.date}
                          onChange={(date) => updateDate(day.id, date)}
                          className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                          dateFormat="MMM d, yyyy"
                        />
                      </div>
                      {planDays.length > 1 && (
                        <button
                          onClick={() => removeDay(day.id)}
                          className="ml-4 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors font-medium text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {/* Nutritional Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
                      <div className="text-sm">
                        <span style={{ color: 'rgb(112, 112, 112)' }}>Calories:</span>
                        <p className="font-bold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(dayTotals.calories)}</p>
                      </div>
                      <div className="text-sm">
                        <span style={{ color: 'rgb(112, 112, 112)' }}>Protein:</span>
                        <p className="font-bold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(dayTotals.protein)}g</p>
                      </div>
                      <div className="text-sm">
                        <span style={{ color: 'rgb(112, 112, 112)' }}>Carbs:</span>
                        <p className="font-bold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(dayTotals.carbs)}g</p>
                      </div>
                      <div className="text-sm">
                        <span style={{ color: 'rgb(112, 112, 112)' }}>Fat:</span>
                        <p className="font-bold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(dayTotals.fat)}g</p>
                      </div>
                    </div>

                    {/* Meal Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {mealTypes.map(mealType => (
                        <div key={mealType} className="border rounded-lg p-4" style={{ borderColor: 'rgba(24, 24, 24, 0.1)', backgroundColor: day.meals[mealType] ? 'rgba(124, 58, 237, 0.05)' : 'white' }}>
                          <label className="body-medium font-semibold mb-2 block" style={{ color: 'rgb(24, 24, 24)' }}>
                            {mealEmojis[mealType]} {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                          </label>

                          {day.meals[mealType]?.length > 0 ? (
                            <div className="space-y-2">
                              {day.meals[mealType].map((meal, idx) => (
                                <div key={`${meal.id}-${idx}`} className="p-3 rounded bg-purple-50 border-l-4 border-purple-600">
                                  <p className="font-semibold text-purple-900">{meal.name}</p>
                                  <p className="text-xs text-purple-700">{meal.calories}cal | P:{meal.protein}g</p>
                                  <div className="flex gap-2 mt-2">
                                    <button
                                      onClick={() => handleViewRecipe(meal)}
                                      className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                                    >
                                      👩‍🍳 Recipe
                                    </button>
                                    <button
                                      onClick={() => removeMeal(day.id, mealType, meal.id)}
                                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 italic mb-2">No meals added</p>
                          )}

                          {/* Meal Addition Section */}
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 relative">
                                <SearchableSelect
                                  options={foods
                                    .filter(food => !day.meals[mealType]?.some(m => m.id === food.id))
                                    .map(food => ({
                                      value: food.id,
                                      label: `${food.name} (${food.calories} cal)`,
                                      ...food
                                    }))}
                                  value={null}
                                  onChange={(selected) => {
                                    if (selected) {
                                      addMeal(day.id, mealType, selected);
                                      // Clear the search input
                                      setDropdownSearch(prev => ({
                                        ...prev,
                                        [`${day.id}-${mealType}`]: ''
                                      }));
                                      setOpenDropdown(null);
                                    }
                                  }}
                                  placeholder={`Search ${mealType} items...`}
                                  dropdownKey={`${day.id}-${mealType}`}
                                  openDropdown={openDropdown}
                                  setOpenDropdown={setOpenDropdown}
                                  searchValue={dropdownSearch}
                                  setSearchValue={setDropdownSearch}
                                />
                              </div>
                              <button
                                onClick={() => {
                                  const searchValue = dropdownSearch[`${day.id}-${mealType}`] || '';
                                  const availableFoods = foods.filter(food => 
                                    !day.meals[mealType]?.some(m => m.id === food.id) &&
                                    food.name.toLowerCase().includes(searchValue.toLowerCase())
                                  );
                                  
                                  if (availableFoods.length > 0) {
                                    addMeal(day.id, mealType, availableFoods[0]);
                                    setDropdownSearch(prev => ({
                                      ...prev,
                                      [`${day.id}-${mealType}`]: ''
                                    }));
                                    setOpenDropdown(null);
                                  } else {
                                    toast.info('No matching food found. Try a different search.');
                                  }
                                }}
                                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm whitespace-nowrap"
                              >
                                Add
                              </button>
                            </div>
                            
                            {day.meals[mealType]?.length > 0 && (
                              <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                                <span>
                                  {day.meals[mealType].length} item{day.meals[mealType].length !== 1 ? 's' : ''} added
                                </span>
                                <button
                                  onClick={() => {
                                    // Clear all meals for this meal type
                                    setPlanDays(planDays.map(d => 
                                      d.id === day.id 
                                        ? { ...d, meals: { ...d.meals, [mealType]: [] }}
                                        : d
                                    ));
                                  }}
                                  className="text-red-500 hover:text-red-700 text-xs"
                                >
                                  Clear all
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Add Another Day Button */}
              <button
                onClick={addDay}
                className="w-full py-4 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl hover:shadow-lg transition-all font-semibold text-lg"
              >
                + Add Another Day
              </button>

              {/* Save Button */}
              <div className="w-full">
                <button 
                  onClick={guestMode ? () => navigate('/login') : savePlan} 
                  className={`w-full py-4 px-6 text-white rounded-2xl transition-all font-semibold text-lg ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : guestMode 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-green-600 hover:bg-green-700'
                  }`}
                  disabled={loading}
                  title={guestMode ? "Sign in to save" : ""}
                >
                  {loading ? 'Saving...' : (guestMode ? '🔒 Sign In to Save Plan' : '💾 Save Plan')}
                </button>
                {guestMode && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                    <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-800">Sign in required to save meal plans</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        <button 
                          onClick={() => navigate('/login')}
                          className="underline hover:text-yellow-900 font-medium"
                        >
                          Create an account or sign in
                        </button> to save your meal plans and track your progress.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recipe Modal */}
      <RecipeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} recipe={selectedRecipe} />
    </div>
  );
};

export default CustomWeeklyPlanPage;

